import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  Calendar,
  Flag,
  FolderKanban,
  Lightbulb,
  Loader2,
  Package,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useCreateMilestone } from "../hooks/useMilestones";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
  meetingId?: boolean;
  ideaId?: boolean;
};

type PrefillContext = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  ideaId?: string | null;
  ideaTitle?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: PrefillContext;
};

export function NewMilestoneDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
}: Props) {
  const { user } = useAuth();
  const createMilestone = useCreateMilestone();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [status, setStatus] = useState("not_started");
  const [progress, setProgress] = useState(0);
  const [isFlagged, setIsFlagged] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    prefill?.companyId ?? "",
  );
  const [selectedProductId, setSelectedProductId] = useState(
    prefill?.productId ?? "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    prefill?.projectId ?? "",
  );
  const [selectedMeetingId, setSelectedMeetingId] = useState(
    prefill?.meetingId ?? "",
  );
  const [selectedIdeaId, setSelectedIdeaId] = useState(prefill?.ideaId ?? "");
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState(user?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(prefill?.companyId ?? "");
      setSelectedProductId(prefill?.productId ?? "");
      setSelectedProjectId(prefill?.projectId ?? "");
      setSelectedMeetingId(prefill?.meetingId ?? "");
      setSelectedIdeaId(prefill?.ideaId ?? "");
    }
  }, [
    isOpen,
    prefill?.companyId,
    prefill?.productId,
    prefill?.projectId,
    prefill?.meetingId,
    prefill?.ideaId,
  ]);

  useEffect(() => {
    if (user?.id && !ownerId) setOwnerId(user.id);
  }, [user?.id, ownerId]);

  useEffect(() => {
    if (progress === 100 && status !== "completed") {
      setStatus("completed");
    }
  }, [progress, status]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const allMeetings = meetingsResult?.data ?? [];

  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const allIdeas = ideasResult?.data ?? [];

  const { data: categoryOptions = [] } = useCategoryItems("milestone_category");

  function reset() {
    setTitle("");
    setDescription("");
    setCategory("");
    setPriority("medium");
    setStatus("not_started");
    setProgress(0);
    setIsFlagged(false);
    setStartDate("");
    setDueDate("");
    setSelectedCompanyId(prefill?.companyId ?? "");
    setSelectedProductId(prefill?.productId ?? "");
    setSelectedProjectId(prefill?.projectId ?? "");
    setSelectedMeetingId(prefill?.meetingId ?? "");
    setSelectedIdeaId(prefill?.ideaId ?? "");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setOwnerId(user?.id ?? "");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    try {
      const res = await createMilestone.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        category: category || null,
        status,
        progress,
        priority,
        isFlagged,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        companyId: selectedCompanyId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        phaseId: selectedPhaseId,
        stageId: selectedStageId,
        meetingId: selectedMeetingId || null,
        ideaId: selectedIdeaId || null,
        ownerId: ownerId || null,
      });
      onCreated?.(res.milestone.code);
      reset();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create milestone",
      );
    }
  }

  if (!isOpen) return null;

  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;
  const lockMeeting = !!prefill?.locked?.meetingId && !!prefill?.meetingId;
  const lockIdea = !!prefill?.locked?.ideaId && !!prefill?.ideaId;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            New Milestone
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Milestone Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="mls-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="mls-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter milestone title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mls-desc">Description</Label>
              <textarea
                id="mls-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the milestone (optional)"
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mls-category">Category</Label>
                <Select
                  id="mls-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">No category</option>
                  {categoryOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Flagged</Label>
                <button
                  type="button"
                  onClick={() => setIsFlagged((v) => !v)}
                  className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm"
                  style={{
                    borderColor: isFlagged
                      ? "hsl(40,95%,60%)"
                      : "hsl(210,30%,85%)",
                    background: isFlagged ? "hsl(40,95%,95%)" : "white",
                    color: isFlagged
                      ? "hsl(40,95%,30%)"
                      : "hsl(215,16%,47%)",
                  }}
                >
                  <Flag
                    className="h-4 w-4"
                    strokeWidth={1.5}
                    style={{
                      fill: isFlagged ? "hsl(40,95%,55%)" : "none",
                      color: isFlagged ? "hsl(40,95%,45%)" : undefined,
                    }}
                  />
                  {isFlagged ? "Flagged" : "Not flagged"}
                </button>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Status &amp; Progress
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mls-status">Status</Label>
                <Select
                  id="mls-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="at_risk">At Risk</option>
                  <option value="delayed">Delayed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mls-priority">Priority</Label>
                <Select
                  id="mls-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mls-progress">Progress: {progress}%</Label>
              <input
                id="mls-progress"
                type="range"
                min={0}
                max={100}
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                style={{ width: "100%", accentColor: "hsl(210,100%,45%)" }}
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mls-start">Start Date</Label>
                <Input
                  id="mls-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mls-due">Due Date</Label>
                <Input
                  id="mls-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="mls-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mls-company"
                  value={selectedCompanyId}
                  disabled={lockCompany}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mls-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mls-product"
                  value={selectedProductId}
                  disabled={lockProduct}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mls-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mls-project"
                  value={selectedProjectId}
                  disabled={lockProject}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PhaseStagePicker
              projectId={selectedProjectId || null}
              phaseId={selectedPhaseId}
              stageId={selectedStageId}
              onPhaseChange={setSelectedPhaseId}
              onStageChange={setSelectedStageId}
            />

            <div className="space-y-2">
              <Label htmlFor="mls-meeting">Meeting</Label>
              <div className="relative">
                <Calendar
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mls-meeting"
                  value={selectedMeetingId}
                  disabled={lockMeeting}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No meeting</option>
                  {allMeetings.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mls-idea">Parent Idea</Label>
              <div className="relative">
                <Lightbulb
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mls-idea"
                  value={selectedIdeaId}
                  disabled={lockIdea}
                  onChange={(e) => setSelectedIdeaId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No parent idea</option>
                  {allIdeas.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} — {i.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Assignment
            </h3>
            <div className="space-y-2">
              <Label htmlFor="mls-owner">Owner</Label>
              <select
                id="mls-owner"
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createMilestone.isPending}
            >
              {createMilestone.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Milestone
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
